import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { SCRIPT_URL, api, getToken, getStoredUser, saveAuth, clearAuth, onUnauthorized } from "@/lib/api";
import Login from "@/components/Login";
import SettingsModal from "@/components/SettingsModal";
import TelegramModal from "@/components/TelegramModal";
import MonitorDashboard from "@/components/MonitorDashboard";
import UptimeChart from "@/components/UptimeChart";
import TicketsCard from "@/components/TicketsCard";
import ApprovalTimeline from "@/components/ApprovalTimeline";
import SpeedTestCard from "@/components/SpeedTestCard";
import IspLetterCard from "@/components/IspLetterCard";
import ReadinessCard from "@/components/ReadinessCard";
import AppDownloadCard from "@/components/AppDownloadCard";
import UsersCard from "@/components/UsersCard";

const INSTALL_CMD = `curl -fsSL ${SCRIPT_URL} -o ookla-setup.sh && sudo bash ookla-setup.sh`;

function App() {
  const [auth, setAuth] = useState({ token: getToken(), username: getStoredUser(), defaultCreds: false, isOwner: false });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [telegramOpen, setTelegramOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [info, setInfo] = useState(null);
  const [tgConfig, setTgConfig] = useState(null);
  const [tgReminderDismissed, setTgReminderDismissed] = useState(
    () => sessionStorage.getItem("tg-reminder-dismissed") === "1"
  );

  const fetchTgConfig = useCallback(() => {
    api
      .get("/telegram/config")
      .then((res) => setTgConfig(res.data))
      .catch(() => {});
  }, []);

  const logout = useCallback(() => {
    clearAuth();
    setAuth({ token: null, username: null, defaultCreds: false });
  }, []);

  useEffect(() => {
    onUnauthorized.handler = logout;
    return () => {
      onUnauthorized.handler = null;
    };
  }, [logout]);

  useEffect(() => {
    if (!auth.token) return;
    api
      .get("/auth/me")
      .then((res) => {
        setAuth((a) => ({
          ...a,
          username: res.data.username,
          defaultCreds: res.data.default_creds,
          isOwner: !!res.data.is_owner,
        }));
        // The install script is owner-only — fetch its info only for the owner
        if (res.data.is_owner) {
          api
            .get("/script-info")
            .then((r2) => setInfo(r2.data))
            .catch(() => {});
        }
      })
      .catch(() => {});
    fetchTgConfig();
  }, [auth.token, fetchTgConfig]);

  // Re-check telegram config when the modal closes (user may have connected)
  useEffect(() => {
    if (!telegramOpen && auth.token) fetchTgConfig();
  }, [telegramOpen, auth.token, fetchTgConfig]);

  const dismissTgReminder = () => {
    sessionStorage.setItem("tg-reminder-dismissed", "1");
    setTgReminderDismissed(true);
  };

  const showTgReminder =
    tgConfig && (!tgConfig.configured || !tgConfig.enabled) && !tgReminderDismissed;

  const handleLogin = (token, username, defaultCreds) => {
    saveAuth(token, username);
    setAuth({ token, username, defaultCreds });
  };

  const handleCredsUpdated = (token, username) => {
    saveAuth(token, username);
    setAuth((a) => ({ ...a, token, username, defaultCreds: false }));
  };

  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(INSTALL_CMD);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = INSTALL_CMD;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  if (!auth.token) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="page" dir="rtl">
      <div className="bg-glow" />

      {/* ===== Top bar ===== */}
      <div className="topbar" data-testid="topbar">
        <div className="topbar-brand">
          <span className="brand-badge sm" data-testid="brand-badge">
            <span className="brand-dot" /> TWIN TC LLC
          </span>
          <h1 className="topbar-title">
            لوحة مراقبة سيرفر <span className="grad">Ookla</span>
          </h1>
        </div>
        <div className="topbar-actions">
          <span className="user-chip" data-testid="topbar-username">👤 {auth.username}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setTelegramOpen(true)} data-testid="telegram-btn">
            🔔 تيليجرام
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setSettingsOpen(true)} data-testid="settings-btn">
            ⚙️ الإعدادات
          </button>
          <button className="btn btn-ghost btn-sm" onClick={logout} data-testid="logout-btn">
            🚪 خروج
          </button>
        </div>
      </div>

      {auth.defaultCreds && (
        <div className="default-creds-banner" data-testid="default-creds-banner">
          ⚠️ أنت تستخدم البيانات الافتراضية (admin/admin) —
          <button className="link-btn" onClick={() => setSettingsOpen(true)} data-testid="change-creds-link">
            غيّرها الآن
          </button>
        </div>
      )}

      {showTgReminder && (
        <div className="tg-reminder-banner" data-testid="telegram-reminder-banner">
          <span className="tg-reminder-text">
            🔔 <b>تنبيهات تيليجرام غير مفعّلة</b> — فعّلها الآن حتى لا تفوتك إشعارات موافقة Ookla وردود التذاكر
          </span>
          <div className="tg-reminder-actions">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setTelegramOpen(true)}
              data-testid="telegram-reminder-setup-btn"
            >
              ⚡ إعداد تيليجرام
            </button>
            <button
              className="tg-reminder-close"
              onClick={dismissTgReminder}
              aria-label="إغلاق التذكير"
              data-testid="telegram-reminder-dismiss-btn"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        username={auth.username}
        onUpdated={handleCredsUpdated}
      />

      <TelegramModal open={telegramOpen} onClose={() => setTelegramOpen(false)} />

      {/* ===== Target strip ===== */}
      <div className="target-strip" data-testid="target-strip">
        <span className="target-chip" dir="ltr">🌐 ookla.twin-tc.net</span>
        <span className="target-chip" dir="ltr">🖥 78.155.66.250</span>
        <span className="target-chip">📍 Damascus, Syria</span>
        <span className="target-chip">⏱ فحص كل 60 ثانية</span>
      </div>

      {/* ===== Live Monitor ===== */}
      <MonitorDashboard />

      {/* ===== Ookla Readiness (aggregated) ===== */}
      <ReadinessCard />

      {/* ===== Android App Download ===== */}
      <AppDownloadCard />

      {/* ===== Approval Journey Timeline ===== */}
      <ApprovalTimeline />

      {/* ===== Speed Test (runs on the server) ===== */}
      <SpeedTestCard />

      {/* ===== Ookla Support Tickets ===== */}
      <TicketsCard />

      {/* ===== ISP Letter (port 5060 unblock request) ===== */}
      <IspLetterCard />

      {/* ===== Uptime & Latency charts ===== */}
      <UptimeChart />

      {/* ===== User management (owner only) ===== */}
      {auth.isOwner && <UsersCard />}

      {/* ===== Install tools (owner only, collapsed) ===== */}
      {auth.isOwner && (
        <section className="card tools-card">
          <button className="tools-toggle" onClick={() => setToolsOpen((o) => !o)} data-testid="tools-toggle">
            <span>🛠 سكربت التثبيت <span className="owner-chip sm">🔒 للمالك فقط</span></span>
            <span className="tools-arrow">{toolsOpen ? "\u25B2" : "\u25BC"}</span>
          </button>
          {toolsOpen && (
            <div className="tools-body" data-testid="tools-body">
              <div className="terminal" dir="ltr" data-testid="install-command">
                <div className="terminal-bar">
                  <span className="t-dot red" />
                  <span className="t-dot yellow" />
                  <span className="t-dot green" />
                  <span className="t-title">root@ubuntu-server</span>
                </div>
                <pre className="terminal-body">
                  <code>
                    <span className="prompt">$ </span>
                    {INSTALL_CMD}
                  </code>
                </pre>
              </div>
              <div className="actions">
                <button className="btn btn-primary" onClick={copyCommand} data-testid="copy-command-btn">
                  {copied ? "\u2714 تم النسخ!" : "\uD83D\uDCCB نسخ الأمر"}
                </button>
                <a
                  className="btn btn-ghost"
                  href={SCRIPT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-script-btn"
                >
                  ⬇ تحميل السكربت
                </a>
              </div>
              {info && (
                <div className="integrity" dir="ltr" data-testid="script-integrity">
                  <span className="integrity-label">SHA-256</span>
                  <code className="integrity-hash">{info.sha256}</code>
                  <span className="integrity-size">{(info.size_bytes / 1024).toFixed(1)} KB</span>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <footer className="footer">
        <span>TWIN TC LLC © {new Date().getFullYear()}</span>
      </footer>
    </div>
  );
}

export default App;
